/**
 * Справочник блюд — главный экран сервиса (§8.1 ТЗ).
 *
 * Сценарий, под который он сделан, ровно один: официант стоит у стола,
 * гость спрашивает про орехи, и на ответ есть десять секунд. Отсюда всё,
 * что здесь выглядит непривычно:
 *
 * - **строка поиска сразу**, без вложенных меню и категорий. Навигация по
 *   разделам — не основной путь;
 * - **бейджи аллергенов в строке результата**, до открытия карточки: их
 *   присылает сервер вместе со строкой, и второго запроса не бывает;
 * - **фильтр «без X» отдельной строкой** — сценарий §8.3, когда гость
 *   называет аллергию, а не блюдо;
 * - **строку про аллергены пишет сервер**. «Состав не размечен» и
 *   «Аллергенов нет» — разные ответы (пункт приёмки 12), и выбирать между
 *   ними в браузере нельзя: ошибка здесь стоит здоровья гостя.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Empty, ErrorState, Loading } from '@shell/index';
import { studyApi } from '../api';
import type { AllergenBadge, DishCard, DishRow } from '../api';

/** Показывать выдачу начинаем с трёх символов (§8.2) — раньше она
 *  бесполезна, а запрос на каждую букву греет сервер зря. */
const MIN_QUERY = 3;

function Badges({ items }: { items: AllergenBadge[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <span className="faint">
      {items.map((item) => `${item.icon ?? ''} ${item.name}`.trim()).join(' · ')}
    </span>
  );
}

/** Плашка «ДЕМО» (D90). Текст приходит с сервера — здесь только
 *  место, где он показывается. */
function DemoBadge({ text }: { text: string | null }) {
  if (!text) {
    return null;
  }
  return <span className="demo-badge">{text}</span>;
}

function DishLine({ row, onOpen }: { row: DishRow; onOpen: (id: number) => void }) {
  return (
    <div className="dish-line">
      <button type="button" className="dish-line__name" onClick={() => onOpen(row.id)}>
        <DemoBadge text={row.demo_badge} />
        {row.name}
      </button>
      <div className="dish-line__meta">
        {row.allergens.length > 0 ? (
          <Badges items={row.allergens} />
        ) : (
          <span className="faint">{row.allergen_summary}</span>
        )}
      </div>
    </div>
  );
}

function DishDetails({ dish, onBack }: { dish: DishCard; onBack: () => void }) {
  const techCard = useQuery({
    queryKey: ['tech-card', dish.id],
    queryFn: () => studyApi.techCard(dish.id),
    // Техкарту спрашиваем только когда её действительно открывают: у
    // официанта её нет вовсе, и лишний 403 в консоли пугает зря.
    enabled: false,
    retry: false,
  });

  return (
    <Card
      title={dish.name}
      actions={
        <Button small variant="quiet" onClick={onBack}>
          Назад
        </Button>
      }
    >
      {/* Полоса над всем остальным, включая фотографию: её читают
          прежде, чем пересказывать состав гостю (§8.7). */}
      {dish.demo_notice && <p className="demo-notice">{dish.demo_notice}</p>}

      {dish.photos.length > 0 && (
        <img className="dish-photo" src={dish.photos[0]} alt={dish.name} />
      )}

      <p>{dish.guest_text}</p>

      <p>
        <strong>Аллергены: </strong>
        {dish.allergens.length > 0 ? <Badges items={dish.allergens} /> : dish.allergen_summary}
      </p>

      {dish.ingredients ? (
        <p className="faint">
          Состав: {dish.ingredients.map((item) => item.name).join(', ')}
        </p>
      ) : (
        <p className="faint">{dish.composition_text}</p>
      )}

      {dish.serving_notes && (
        <p>
          <strong>Подача: </strong>
          {dish.serving_notes}
        </p>
      )}
      {dish.pairing_notes && (
        <p>
          <strong>С чем подать: </strong>
          {dish.pairing_notes}
        </p>
      )}

      {dish.tech_card_available && (
        <>
          <Button small onClick={() => techCard.refetch()}>
            Техкарта
          </Button>
          {/* Дословно из базы: ни пересказа, ни пересчёта единиц (§8.5).
              `pre` здесь не украшение — техкарта построчная, и склеенные
              строки меняют смысл закладки. */}
          {techCard.data && <pre className="tech-card">{techCard.data.body}</pre>}
        </>
      )}
    </Card>
  );
}

/**
 * Необязательные `dishId` и `onOpenDish` — открытая карточка снаружи: у
 * портала она в адресе (`/study/catalog/42`), чтобы ссылка из бота вела
 * прямо на блюдо, а «назад» браузера закрывал карточку. Без них — прежнее
 * состояние экрана, как в Mini App.
 */
export function Catalog({
  dishId,
  onOpenDish,
}: {
  dishId?: number | null;
  onOpenDish?: (id: number | null) => void;
} = {}) {
  const [query, setQuery] = useState('');
  const [without, setWithout] = useState<string[]>([]);
  const [localId, setLocalId] = useState<number | null>(null);
  const openId = onOpenDish ? (dishId ?? null) : localId;
  const setOpenId = onOpenDish ?? setLocalId;
  const lastSearch = useRef(`|`);

  const allergens = useQuery({
    queryKey: ['allergens'],
    queryFn: studyApi.allergens,
    staleTime: Infinity,
  });

  // Отдельный запрос, а не поле в выдаче поиска: баннер нужен и на пустом
  // экране «введите три буквы», то есть до первого поиска (§8.7).
  const demo = useQuery({
    queryKey: ['demo-state'],
    queryFn: studyApi.demoState,
    staleTime: Infinity,
  });

  const active = query.trim().length >= MIN_QUERY || without.length > 0;
  const search = useQuery({
    queryKey: ['dishes', query.trim(), without.join(',')],
    queryFn: () => studyApi.searchDishes(query.trim(), without),
    enabled: active,
  });

  const dish = useQuery({
    queryKey: ['dish', openId],
    queryFn: () => studyApi.dish(openId as number),
    enabled: openId !== null,
  });

  useEffect(() => {
    // Смена запроса закрывает открытую карточку: иначе человек ищет новое
    // блюдо и видит старое. Сравнение с прошлым запросом, а не «первый
    // запуск»: карточка, открытая ссылкой, закрываться при появлении
    // экрана не должна (и при двойном запуске эффектов в StrictMode тоже).
    const key = `${query}|${without.join(',')}`;
    if (lastSearch.current === key) return;
    lastSearch.current = key;
    setOpenId(null);
  }, [query, without]);

  const chips = useMemo(() => allergens.data?.allergens ?? [], [allergens.data]);

  const demoBanner = demo.data?.dishes ? demo.data.banner : null;

  if (openId !== null) {
    if (dish.isPending) return <Loading />;
    if (dish.error || !dish.data)
      return <ErrorState error={dish.error} onRetry={() => dish.refetch()} />;
    return <DishDetails dish={dish.data} onBack={() => setOpenId(null)} />;
  }

  return (
    <>
      {/* Баннер виден и до первого поиска — на пустом экране тоже. */}
      {demoBanner && <p className="demo-banner">{demoBanner}</p>}

      <Card>
        <input
          className="search"
          type="search"
          inputMode="search"
          autoFocus
          placeholder="Название блюда или ингредиент"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="chips">
          {chips.map((item) => {
            const on = without.includes(item.code);
            return (
              <Button
                key={item.code}
                small
                variant={on ? 'primary' : 'quiet'}
                onClick={() =>
                  setWithout((current) =>
                    on ? current.filter((code) => code !== item.code) : [...current, item.code],
                  )
                }
              >
                без «{item.name}»
              </Button>
            );
          })}
        </div>
      </Card>

      {!active && (
        <Empty
          title="Введите три буквы"
          text="Поиск идёт и по названию, и по составу: «креветк» найдёт «Креветки темпура»."
        />
      )}

      {active && search.isPending && <Loading />}
      {active && search.error && (
        <ErrorState error={search.error} onRetry={() => search.refetch()} />
      )}

      {active && search.data && (
        <Card>
          {search.data.note && <p className="faint">{search.data.note}</p>}
          {search.data.dishes.length === 0 ? (
            <Empty
              title={search.data.empty_text ?? 'Ничего не найдено'}
              text={
                search.data.suggestions.length > 0
                  ? `Может быть, вы искали: ${search.data.suggestions.join(', ')}`
                  : undefined
              }
            />
          ) : (
            search.data.dishes.map((row) => (
              <DishLine key={row.id} row={row} onOpen={setOpenId} />
            ))
          )}
        </Card>
      )}
    </>
  );
}
